import { ENVIRONMENT, isDevelopment } from '@app/environment';
import { InputErrorTemplate } from '@core/errors/error-template';
import { AppErrors } from '@core/errors/errors';
import {
  EmailValidator,
  FirebaseAuthValidator,
  FirebasePasswordValidator,
  NetworkErrorValidator,
  PasswordValidator,
} from '@core/errors/generic-errors';
import { PrefetchService } from '@core/prefetch/prefetch.service';
import { Inject } from '@rxdi/core';
import { Form, FormGroup } from '@rxdi/forms';
import { classMap, Component, css, html, property, state } from '@rxdi/lit-html';
import { Router } from '@rxdi/router';
import { InputStyle } from '@rxdi/ui-kit/styles/form/input';
import { BaseComponentWithLoading } from '@shared/loading/loading.component';
import { SocialService } from '@stradivario/graphql-server';

import { LoginFormModel } from './login.model';

/**
 * @customElement login-component
 */
@Component({
  selector: 'login-component',
  style: css`
    ${InputStyle}

    input[type='text'],
  input[type='email'],
  input[type='number'],
  textarea,
  fieldset {
      /* required to properly style form
   elements on WebKit based browsers */
      -webkit-appearance: none;

      width: 100%;
      margin: 0;

      font-family: inherit;
      font-size: 90%;

      -moz-box-sizing: border-box;
      box-sizing: border-box;
    }
    textarea,
    select,
    input,
    button {
      outline: none;
    }

    .is-invalid {
      box-shadow: 0 0 1px 1px #c95454;
    }
    /* 
  input:focus {
   box-shadow: 0 0 1px 1px #c78282;
  } */
    .container {
      padding: 50px;
      margin: 0px auto;
      margin-top: 150px;
      margin-bottom: 0px;
      width: 440px;
      background-color: #f8f8f8;
      border-radius: 15px;
      box-shadow: rgb(162 162 162 / 25%) 0px 0px 3px, rgb(0 0 0 / 22%) 0px 0px 3px;
    }
    p {
      color: black;
      padding: 0;
      margin: 0;
    }

    input[type='password']:disabled {
      background: #a9a9a9;
    }
    input[type='password']:disabled::placeholder {
      color: white;
    }
  `,
  template(this: LoginComponent) {
    return html`
      ${this.getLoadingTemplate()}
      <div class="container">
        <div class="form-group card card-container">
          <div style="text-align: center;margin-bottom: 20px;">
            <p style="font-weight: 400;font-size: 2em">Welcome Back</p>
          </div>
          <div
            @mouseenter=${() => this.prefetch.fetch(import('../projects/projects.module'))}
            style="margin: 0 auto; width: 220px;"
          >
            <social-login-container></social-login-container>
          </div>
          <form
            name="login-form"
            @submit=${async (data) => {
              console.log(data);
            }}
          >
            <p>Email</p>
            <input
              name="email"
              required
              type="email"
              .value=${this.model.email}
              class=${classMap({
                'rx-input': true,
              })}
              placeholder="Email address"
            />
            <div style="height:30px;">${InputErrorTemplate(this.form.get('email'))}</div>
            <p>Password</p>
            <input
              type="password"
              .value=${this.model.password}
              name="password"
              required
              class="rx-input"
              placeholder="Password"
            />
            <div style="height: 30px;">${InputErrorTemplate(this.form.get('password'))}</div>
            <rx-button
              palette="primary"
              style="width: 100%"
              @mouseenter=${() => this.prefetch.fetch(import('../projects/projects.module'))}
              type="submit"
            >
              Sign in
            </rx-button>
            <rx-button
              style="width:100%; margin-top: 10px;"
              @click=${async () => {
                const email = this.form.getValue('email') as never;
                if (!email) {
                  this.form.get('email').disabled = true;
                  return await this.form.updateValueAndValidity();
                }
                const returnUrl = isDevelopment()
                  ? `http://localhost:1234/auth/${email}`
                  : `http://${ENVIRONMENT.DOMAIN_NAME}:1234/auth/${email}`;
                this.socialService.sendSignInLinkToEmail(email, returnUrl).subscribe(
                  () => {
                    this.router.go(`/signup/success/${email}`);
                  },
                  (e) => console.error(e)
                );
              }}
              >Send Magic link
            </rx-button>
          </form>
          <a style="color: #359ec0; text-decoration: none;font-size: 13px;" href="/reset-password">
            Forgot the password?
          </a>
        </div>
      </div>
      <div style="text-align: center; margin-bottom: 30px; margin-top: 10px;">
        Need an Account?<a style="cursor: pointer;color: #359ec0" @click=${() => this.router.go('/signup')}>
          Sign up here.</a
        >
      </div>
    `;
  },
})
export class LoginComponent extends BaseComponentWithLoading {
  @property({ type: Object }) error: unknown;

  @Router()
  private router: Router;

  @state()
  password = 'none';

  @Inject(SocialService)
  private socialService: SocialService;

  @Inject(PrefetchService)
  private prefetch: PrefetchService;

  @property()
  private model = new LoginFormModel();

  @Form({
    strategy: 'change',
    name: 'login-form',
    strict: true,
    model: 'model',
  })
  private form = new FormGroup(
    {
      password: [this.model.password, [PasswordValidator, FirebasePasswordValidator]],
      email: [this.model.email, [NetworkErrorValidator, EmailValidator, FirebaseAuthValidator]],
    },
    AppErrors
  );

  onSubmit() {
    if (this.isLoading()) {
      return;
    }
    this.showLoading();
    this.socialService
      .signInWithEmailAndPassword(this.form.value.email as never, this.form.value.password as never)
      .subscribe(
        () => {
          this.hideLoading();
          this.router.go('/projects');
        },
        async (e) => {
          this.hideLoading();
          this.error = e;
          await this.form.updateValueAndValidity();
          this.error = '';
        }
      );
  }

  async hasErrors() {
    return (await this.form.updateValueAndValidity()).length;
  }
}
